import { FIREBASE_PATHS } from '../config/firebase';
import { buildRemoteLeaderboardEntry, decodeLeaderboardEntry } from '../leaderboard/decoder';
import { findRank, LEADERBOARD_LIMIT, LEADERBOARD_RETENTION_LIMIT, sortLeaderboardEntries } from '../leaderboard/ranking';
import type {
  LeaderboardBoard,
  LeaderboardEntry,
  LeaderboardSubmitResult,
  RankedLeaderboardEntry,
} from '../leaderboard/types';
import type { RealtimeDatabasePort } from './firebase';
import { parseJson, type StoragePort } from './storage';
import { PersistentUploadQueue } from './upload-queue';

const LOCAL_KEY = 'slot-yugioh-rework:season3:v2:leaderboards';
const QUEUE_KEY = 'slot-yugioh-rework:season3:v2:leaderboard-upload-queue';

interface LeaderboardUpload {
  readonly board: LeaderboardBoard;
  readonly entry: LeaderboardEntry;
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

function isSeasonThreeWireEntry(value: unknown): boolean {
  return record(value)?.seasonId === 'season3';
}

function pathFor(board: LeaderboardBoard): string {
  return board === 'runs' ? FIREBASE_PATHS.seasonXRuns : FIREBASE_PATHS.seasonXFreeSpins;
}

function sameEntry(left: LeaderboardEntry, right: LeaderboardEntry): boolean {
  return left.clientEntryId === right.clientEntryId && left.name === right.name
    && left.betCents === right.betCents && left.profitCents === right.profitCents
    && left.createdAt === right.createdAt && left.board === right.board;
}

export interface LeaderboardServicePort {
  local(board: LeaderboardBoard): readonly RankedLeaderboardEntry[];
  refresh(board: LeaderboardBoard): Promise<readonly RankedLeaderboardEntry[]>;
  submit(entry: LeaderboardEntry): Promise<LeaderboardSubmitResult>;
  flushPending(): Promise<number>;
}

export class LeaderboardService implements LeaderboardServicePort {
  private readonly entries = new Map<LeaderboardBoard, LeaderboardEntry[]>([['runs', []], ['freeSpins', []]]);
  private readonly queue: PersistentUploadQueue<LeaderboardUpload>;

  constructor(
    private readonly storage: StoragePort,
    private readonly remote: RealtimeDatabasePort,
  ) {
    this.restore();
    this.queue = new PersistentUploadQueue(storage, QUEUE_KEY, {
      decode: (value) => {
        const source = record(value);
        if (!source || (source.board !== 'runs' && source.board !== 'freeSpins')) return null;
        const entry = decodeLeaderboardEntry(source.entry, { board: source.board, seasonId: 'season3' });
        return entry ? { board: source.board, entry } : null;
      },
    });
  }

  local(board: LeaderboardBoard): readonly RankedLeaderboardEntry[] {
    return sortLeaderboardEntries(this.entries.get(board) ?? [], LEADERBOARD_LIMIT);
  }

  async refresh(board: LeaderboardBoard): Promise<readonly RankedLeaderboardEntry[]> {
    const availability = await this.remote.connect();
    if (availability.status !== 'online') return this.local(board);
    const children = await this.remote.readChildren(pathFor(board), { orderByChild: 'xValue', limitToLast: LEADERBOARD_RETENTION_LIMIT });
    const remoteEntries = children
      .filter((child) => isSeasonThreeWireEntry(child.value))
      .map((child) => decodeLeaderboardEntry(child.value, { board, seasonId: 'season3', fallbackId: child.key }))
      .filter((entry): entry is LeaderboardEntry => entry !== null);
    this.entries.set(board, [...sortLeaderboardEntries(remoteEntries, LEADERBOARD_RETENTION_LIMIT)]);
    this.persist();
    return this.local(board);
  }

  async submit(value: LeaderboardEntry): Promise<LeaderboardSubmitResult> {
    const entry = decodeLeaderboardEntry(value, { board: value.board, seasonId: 'season3', fallbackId: value.id });
    if (!entry || entry.seasonId !== 'season3') throw new TypeError('Invalid Season 3 leaderboard entry.');
    this.mergeLocal(entry);
    this.queue.enqueue(entry.clientEntryId, { board: entry.board, entry });
    const confirmed = await this.uploadOne({ board: entry.board, entry });
    let globalRank: number | null = null;
    if (confirmed) {
      const remoteEntries = await this.loadRemote(entry.board);
      if (remoteEntries) globalRank = findRank(remoteEntries, entry.clientEntryId);
    }
    return Object.freeze({
      entry, localRank: findRank(this.entries.get(entry.board) ?? [], entry.clientEntryId), globalRank,
      remoteConfirmed: confirmed && globalRank !== null,
      queued: this.queue.list().some((item) => item.id === entry.clientEntryId),
    });
  }

  async flushPending(): Promise<number> {
    let confirmed = 0;
    for (const item of this.queue.list()) {
      if (await this.uploadOne(item.payload)) confirmed += 1;
    }
    return confirmed;
  }

  private async uploadOne(upload: LeaderboardUpload): Promise<boolean> {
    const availability = await this.remote.connect();
    if (availability.status !== 'online' || !availability.uid) return false;
    this.queue.attempted(upload.entry.clientEntryId);
    try {
      const payload = buildRemoteLeaderboardEntry(upload.entry, availability.uid);
      const result = await this.remote.createOnly(`${pathFor(upload.board)}/${upload.entry.clientEntryId}`, payload);
      let matches = result.status === 'created';
      if (result.status === 'exists') {
        const existing = decodeLeaderboardEntry(result.existingValue, {
          board: upload.board, seasonId: 'season3', fallbackId: upload.entry.clientEntryId,
        });
        matches = existing !== null && sameEntry(existing, upload.entry);
      }
      if (matches) this.queue.acknowledge(upload.entry.clientEntryId);
      return matches;
    } catch { return false; }
  }

  private async loadRemote(board: LeaderboardBoard): Promise<readonly LeaderboardEntry[] | null> {
    try {
      const children = await this.remote.readChildren(pathFor(board), { orderByChild: 'xValue', limitToLast: LEADERBOARD_RETENTION_LIMIT });
      return children.filter((child) => isSeasonThreeWireEntry(child.value)).map((child) => decodeLeaderboardEntry(child.value, {
        board, seasonId: 'season3', fallbackId: child.key,
      })).filter((entry): entry is LeaderboardEntry => entry !== null);
    } catch { return null; }
  }

  private mergeLocal(entry: LeaderboardEntry): void {
    const current = this.entries.get(entry.board) ?? [];
    this.entries.set(entry.board, [...sortLeaderboardEntries([...current, entry], LEADERBOARD_RETENTION_LIMIT)]);
    this.persist();
  }

  private restore(): void {
    const envelope = record(parseJson(this.storage.getItem(LOCAL_KEY)));
    if (!envelope || envelope.version !== 2) { this.storage.removeItem(LOCAL_KEY); return; }
    for (const board of ['runs', 'freeSpins'] as const) {
      const rawEntries = Array.isArray(envelope[board]) ? envelope[board] : [];
      const decoded = rawEntries.map((entry) => decodeLeaderboardEntry(entry, { board, seasonId: 'season3' }))
        .filter((entry): entry is LeaderboardEntry => entry !== null);
      this.entries.set(board, [...sortLeaderboardEntries(decoded, LEADERBOARD_RETENTION_LIMIT)]);
    }
    this.persist();
  }

  private persist(): void {
    this.storage.setItem(LOCAL_KEY, JSON.stringify({
      version: 2, runs: this.entries.get('runs') ?? [], freeSpins: this.entries.get('freeSpins') ?? [],
    }));
  }
}
